import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Eye, EyeOff, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../ui/switch";

/**
 * Startup-side consent card for the "Debt Intelligence" panel on
 * investor-facing profiles. Flipping this ON exposes bureau band,
 * CAM-Lite ratios, sanction history and lender coverage on
 * /startup/:id — without ever exposing raw score or PAN/bank details.
 *
 * The founder can preview exactly what investors would see via
 * /api/startup/debt-intelligence/preview.
 */
export default function DebtIntelConsent() {
  const [consent, setConsent] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get("/startup/debt-intelligence-consent")
      .then((r) => {
        setConsent(!!r.data.share_with_investors);
        setUpdatedAt(r.data.updated_at);
      })
      .catch(() => {});
  }, []);

  const flip = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post("/startup/debt-intelligence-consent", { share_with_investors: next });
      setConsent(!!data.share_with_investors);
      toast.success(next ? "Debt intelligence is now visible to investors." : "Debt intelligence hidden from investors.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const togglePreview = async () => {
    setShowPreview((v) => !v);
    if (!preview) {
      try {
        const { data } = await api.get("/startup/debt-intelligence/preview");
        setPreview(data);
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  return (
    <div
      className="border border-ink/10 bg-white/70 backdrop-blur rounded-md p-5 mb-6"
      data-testid="startup-debt-intel-consent"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 mb-1">
            <Landmark size={14} className="text-navy" />
            <h3 className="text-sm font-heading text-navy">Debt Intelligence · investor visibility</h3>
            {consent && <ShieldCheck size={13} className="text-emerald-600" />}
          </div>
          <p className="text-xs text-ink-muted leading-relaxed max-w-xl">
            When ON, investors browsing your VenturaZ profile see a curated Debt Intelligence panel —
            bureau band (never the raw score), DSCR, leverage, EBITDA margin, sanction history and
            lender coverage. Your PAN, GST, contact details and raw score are never exposed.
          </p>
          {updatedAt && (
            <div className="text-[10px] font-mono text-ink-muted mt-1">
              Last changed {new Date(updatedAt).toLocaleString()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-heading ${consent ? "text-emerald-700" : "text-ink-muted"}`}>
            {consent ? "Visible to investors" : "Hidden"}
          </span>
          <Switch
            checked={consent}
            onCheckedChange={flip}
            disabled={saving}
            data-testid="startup-debt-intel-toggle"
          />
        </div>
      </div>
      <button
        onClick={togglePreview}
        className="mt-3 text-[11px] font-heading text-navy hover:bg-navy/5 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
        data-testid="startup-debt-intel-preview"
      >
        {showPreview ? <EyeOff size={11} /> : <Eye size={11} />} {showPreview ? "Hide preview" : "Preview what investors see"}
      </button>
      {showPreview && preview && (
        <pre className="mt-2 text-[10px] font-mono bg-ink/5 border border-ink/10 rounded p-3 overflow-auto max-h-[320px]"
              data-testid="startup-debt-intel-preview-json">
          {JSON.stringify(preview, null, 2)}
        </pre>
      )}
    </div>
  );
}
